// src/components/RegisterWindowOrder.js

import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import moment from 'moment-timezone';
import 'moment/locale/es';
import 'react-datepicker/dist/react-datepicker.css';
import '../styles/RegisterWindowOrder.css';

import api from '../api/api';
import { useNavigate } from 'react-router-dom';

const RegisterWindowOrder = () => {
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [windowOrderNumber, setWindowOrderNumber] = useState('');
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [computedTotal, setComputedTotal] = useState('0.00');

  const [formData, setFormData] = useState({
    ejecutivo: '',
    customer: '',
    para: '',
    monto: '',
    iva: 'No',
    detalleEntrega: '',
    recibidoPor: '',
    formaPago: '',
  });

  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  // Carga inicial: clientes + próximo OR-xxx
  useEffect(() => {
    const init = async () => {
      // 1) Clientes
      try {
        const { data: cli } = await api.get('/api/clients');
        setClients(cli || []);
      } catch (err) {
        console.error('Error al obtener clientes:', err);
        if (err?.response?.status === 401) return handleUnauthorized();
      }

      // 2) Próximo OR-xxx (si falla, OR-1)
      try {
        const { data: ord } = await api.get('/api/orders');

        const orSeqs = (ord || [])
          .map((o) => o.orderNumber)
          .filter((n) => n?.startsWith('OR-'))
          .map((n) => parseInt(String(n).split('-')[1], 10))
          .filter((n) => !isNaN(n));

        const next = orSeqs.length ? Math.max(...orSeqs) + 1 : 1;
        setWindowOrderNumber(`OR-${next}`);
      } catch (err) {
        console.error('No se pudo obtener /api/orders para OR-xxx:', err);
        if (err?.response?.status === 401) return handleUnauthorized();
        setWindowOrderNumber('OR-1');
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recalcular total con IVA
  useEffect(() => {
    const base = parseFloat(formData.monto) || 0;
    if (formData.iva === 'Si') {
      const ivaAmt = base * 0.13;
      setComputedTotal((base + ivaAmt).toFixed(2));
    } else {
      setComputedTotal(base.toFixed(2));
    }
  }, [formData.monto, formData.iva]);

  const clientOptions = (clients || []).map((c) => ({
    value: c.name,
    label: c.name,
  }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClientSelect = (option) => {
    setFormData((prev) => ({ ...prev, customer: option?.value || '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!deliveryDate) {
      alert('La fecha y hora de entrega son obligatorias.');
      return;
    }

    setIsSubmitting(true);

    try {
      const scheduledTimeCR = moment(deliveryDate)
        .tz('America/Costa_Rica')
        .format();

      const base = parseFloat(formData.monto) || 0;
      const ivaAmt = formData.iva === 'Si' ? base * 0.13 : 0;

      // Mantengo tu formato de IVA para no cambiar lógica
      const ivaField = formData.iva === 'Si' ? `¢${ivaAmt.toFixed(2)} de iva` : 'No';

      const payload = {
        orderNumber: windowOrderNumber,
        tipoOrden: 'VENTANA',
        fuente: 'N/A',
        ejecutivo: formData.ejecutivo,

        customer: formData.customer || 'vacío',
        para: formData.para || formData.customer || 'vacío',

        // ✅ importante: total como número
        total: base,

        scheduledTime: scheduledTimeCR,
        detalleEntrega: formData.detalleEntrega,
        recibidoPor: formData.recibidoPor,
        formaPago: formData.formaPago,

        iva: ivaField,
        totalPayment: parseFloat(computedTotal) || 0,
      };

      const res = await api.post('/api/orders', payload);

      alert(`Pedido de ventana registrado: ${res?.data?.orderNumber || windowOrderNumber}`);

      // Reset
      setFormData({
        ejecutivo: '',
        customer: '',
        para: '',
        monto: '',
        iva: 'No',
        detalleEntrega: '',
        recibidoPor: '',
        formaPago: '',
      });

      setComputedTotal('0.00');
      setDeliveryDate(null);

      setWindowOrderNumber((prev) => {
        const seq = parseInt(String(prev).split('-')[1], 10) || 0;
        return `OR-${seq + 1}`;
      });
    } catch (err) {
      console.error('Error al registrar pedido de ventana:', err);

      if (err?.response?.status === 401) return handleUnauthorized();

      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'Error al registrar pedido de ventana.';
      alert(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="register-window-order">
      <header className="rw-header">
        <h1>Registrar Pedido Ventana</h1>
        <p>Nº {windowOrderNumber}</p>
      </header>

      <form className="rw-form" onSubmit={handleSubmit}>
        {/* Ejecutivo y Cliente */}
        <div className="rw-form-row">
          <div className="rw-form-group half">
            <label>Ejecutiv@:</label>
            <input
              type="text"
              name="ejecutivo"
              value={formData.ejecutivo}
              onChange={handleChange}
              placeholder="Nombre del ejecutivo"
            />
          </div>
          <div className="rw-form-group half">
            <label>Cliente (opcional):</label>
            <Select
              className="react-select"
              classNamePrefix="react-select"
              options={clientOptions}
              value={clientOptions.find((o) => o.value === formData.customer) || null}
              onChange={handleClientSelect}
              placeholder="Buscar cliente..."
              isClearable
            />
          </div>
        </div>

        {/* Nombre "Para" y Monto */}
        <div className="rw-form-row">
          <div className="rw-form-group half">
            <label>Para:</label>
            <input
              type="text"
              name="para"
              value={formData.para}
              onChange={handleChange}
              placeholder="Nombre destinatario"
            />
          </div>
          <div className="rw-form-group half">
            <label>Monto base:</label>
            <input
              type="number"
              name="monto"
              value={formData.monto}
              onChange={handleChange}
              placeholder="Total base"
            />
          </div>
        </div>

        {/* Entregar el: Fecha y Hora */}
        <div className="rw-form-row">
          <div className="rw-form-group half">
            <label>Entregar el:</label>
            <DatePicker
              selected={deliveryDate}
              onChange={setDeliveryDate}
              showTimeSelect
              timeFormat="hh:mm aa"
              timeIntervals={15}
              dateFormat="dd/MM/yyyy h:mm aa"
              placeholderText="Seleccione fecha y hora"
              className="rw-datepicker-input"
            />
          </div>
        </div>

        {/* IVA y Total */}
        <div className="rw-form-row">
          <div className="rw-form-group half">
            <label>IVA:</label>
            <select name="iva" value={formData.iva} onChange={handleChange}>
              <option value="No">No</option>
              <option value="Si">Si (13%)</option>
            </select>
          </div>
          <div className="rw-form-group half">
            <label>Total calculado:</label>
            <input type="text" name="totalPayment" value={computedTotal} readOnly />
          </div>
        </div>

        {/* Detalle entregado */}
        <div className="rw-form-group">
          <label>Detalle entregado:</label>
          <textarea
            name="detalleEntrega"
            value={formData.detalleEntrega}
            onChange={handleChange}
            placeholder="Descripción breve..."
          />
        </div>

        {/* Recibido por y Forma de Pago */}
        <div className="rw-form-row">
          <div className="rw-form-group half">
            <label>Recibido por:</label>
            <input
              type="text"
              name="recibidoPor"
              value={formData.recibidoPor}
              onChange={handleChange}
              placeholder="Quien recibe"
            />
          </div>
          <div className="rw-form-group half">
            <label>Forma de Pago:</label>
            <select name="formaPago" value={formData.formaPago} onChange={handleChange}>
              <option value="">Seleccione...</option>
              <option value="SINPE">SINPE</option>
              <option value="CRÉDITO">CRÉDITO</option>
              <option value="TRANSFERENCIA BANCARIA">TRANSFERENCIA BANCARIA</option>
              <option value="PAYPAL">PAYPAL</option>
              <option value="WESTERN UNION">WESTERN UNION</option>
              <option value="EFECTIVO">EFECTIVO</option>
              <option value="TARJETA">TARJETA</option>
            </select>
          </div>
        </div>

        <div className="rw-form-actions">
          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Enviando...' : 'Registrar Ventana'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RegisterWindowOrder;
